import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { billingQueries, brl } from "@/lib/billing-api";

export const Route = createFileRoute("/admin/")({
  component: AdminOverviewPage,
});

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return `${MONTHS[Number(m) - 1] ?? m}/${(y ?? "").slice(2)}`;
}

function AdminOverviewPage() {
  const overview = useQuery(billingQueries.adminOverview());
  if (overview.isPending) return <LoadingState rows={6} />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const o = overview.data;
  const kpis = [
    { label: "MRR", value: brl(o.mrrCents), hint: "Assinaturas ativas e com pagamento pendente" },
    { label: "ARR", value: brl(o.arrCents), hint: "MRR × 12" },
    { label: "Novos no mês", value: String(o.newThisMonth), hint: "Workspaces criados" },
    { label: "Churn do mês", value: `${(o.churnRate * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`, hint: `${o.canceledThisMonth} cancelamento(s)` },
  ];
  const totals = [
    { label: "Workspaces", value: o.totals.workspaces },
    { label: "Ativos", value: o.totals.active },
    { label: "Em teste", value: o.totals.trialing },
    { label: "Inadimplentes", value: o.totals.pastDue },
    { label: "Cancelados", value: o.totals.canceled },
    { label: "Em modo leitura", value: o.totals.readOnly },
    { label: "Suspensos", value: o.totals.suspended },
  ];
  return (
    <>
      <PageHeader title="Visão geral" description="Receita recorrente e saúde da base de clientes do IAGENIX Ads." />
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {kpis.map((k) => (
            <Card key={k.label} className="gap-2 py-4">
              <CardHeader className="px-4">
                <CardDescription>{k.label}</CardDescription>
                <CardTitle className="text-2xl tabular-nums">{k.value}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 text-xs text-muted-foreground">{k.hint}</CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {totals.map((t) => (
            <div key={t.label} className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{t.label}</p>
              <p className="text-xl font-semibold tabular-nums">{t.value}</p>
            </div>
          ))}
        </div>
        <Card>
          <CardHeader>
            <CardTitle>MRR por mês</CardTitle>
            <CardDescription>Reconstruído pelas datas de ativação e cancelamento, com o preço atual de cada assinatura.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64" role="img" aria-label="Gráfico de MRR dos últimos 12 meses">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={o.series.map((s) => ({ name: monthLabel(s.month), mrr: s.mrrCents / 100 }))} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.15} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `R$ ${v.toLocaleString("pt-BR")}`} width={80} />
                  <Tooltip formatter={(value) => [brl(Math.round(Number(value ?? 0) * 100)), "MRR"]} />
                  <Bar dataKey="mrr" name="MRR" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Receita por plano</CardTitle>
          </CardHeader>
          <CardContent>
            {o.byPlan.length === 0 ? (
              <EmptyState title="Nenhuma assinatura paga ainda" description="Quando alguém assinar, a receita aparece aqui." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plano</TableHead>
                    <TableHead className="text-right">Assinantes</TableHead>
                    <TableHead className="text-right">MRR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {o.byPlan.map((p) => (
                    <TableRow key={p.code}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.count}</TableCell>
                      <TableCell className="text-right tabular-nums">{brl(p.mrrCents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
